import React from 'react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import GradientCard from '../gamification/GradientCard';
import {
  useNotificationPreferences,
  useUpdateNotificationPreferences,
} from '../../services/settings';

/**
 * Settings → Notifications: the member's own workspace emails.
 *
 * Email notifications (GET/PUT /settings/notifications, per workspace) cover
 * course and program assignment emails. Account emails (password resets,
 * sign-in codes, invitations) are always sent; this switch never governs them.
 * Marketing consent is per person, not per workspace, and lives in the General
 * tab (MarketingConsentSwitch).
 *
 * Only `email_notifications` is offered. The other stored booleans
 * (`push_notifications`, `course_reminders`, `system_updates`) are not read by
 * anything that sends, so a switch for them would do nothing.
 */

interface PreferenceRowProps {
  id: string;
  label: string;
  description: string;
  checked: boolean;
  busy: boolean;
  onChange: (next: boolean) => void;
}

/** One labelled switch; same track as Settings → AI. */
const PreferenceRow: React.FC<PreferenceRowProps> = ({
  id,
  label,
  description,
  checked,
  busy,
  onChange,
}) => (
  <div className="flex items-center justify-between gap-4">
    <div className="min-w-0">
      <p id={`${id}-label`} className="text-sm font-medium text-text-primary">
        {label}
      </p>
      <p id={`${id}-description`} className="mt-0.5 text-xs text-text-secondary">
        {description}
      </p>
    </div>
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-disabled={busy}
      aria-labelledby={`${id}-label`}
      aria-describedby={`${id}-description`}
      onClick={() => {
        if (!busy) onChange(!checked);
      }}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors duration-fast ${
        checked ? 'bg-primary-500' : 'bg-grey-300 dark:bg-grey-700'
      } ${busy ? 'cursor-wait opacity-60' : 'cursor-pointer'}`}
    >
      <span
        className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform duration-fast ${
          checked ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  </div>
);

const RowSkeleton: React.FC<{ label: string }> = ({ label }) => (
  <div
    role="status"
    aria-label={label}
    className="flex animate-pulse items-center justify-between gap-4"
  >
    <div className="space-y-2">
      <div className="h-4 w-40 rounded bg-neutral-200 dark:bg-neutral-700" />
      <div className="h-3 w-64 max-w-full rounded bg-neutral-200 dark:bg-neutral-700" />
    </div>
    <div className="h-6 w-11 shrink-0 rounded-full bg-neutral-200 dark:bg-neutral-700" />
  </div>
);

const LoadError: React.FC<{ what: string; onRetry: () => void }> = ({ what, onRetry }) => (
  <p className="text-sm text-text-secondary">
    Couldn&apos;t load your {what}.{' '}
    <button type="button" onClick={onRetry} className="text-primary-500 hover:underline">
      Retry
    </button>
  </p>
);

const CardLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="mb-4 text-xs font-semibold uppercase tracking-wide text-text-secondary">
    {children}
  </p>
);

const EmailNotificationsCard: React.FC = () => {
  const { data, isLoading, isError, refetch } = useNotificationPreferences();
  const update = useUpdateNotificationPreferences();
  // Show the requested value while saving; a failed save falls back to the server's.
  const checked =
    (update.isPending ? update.variables?.email_notifications : undefined) ??
    data?.email_notifications ??
    false;

  return (
    <GradientCard className="p-6" overlayOpacity="opacity-20 dark:opacity-25">
      <CardLabel>Workspace emails</CardLabel>
      {isLoading ? (
        <RowSkeleton label="Loading notification preferences" />
      ) : isError || !data ? (
        <LoadError what="notification preferences" onRetry={() => refetch()} />
      ) : (
        <PreferenceRow
          id="email-notifications"
          label="Email notifications"
          description="Emails when you're assigned a course or program in this workspace."
          checked={checked}
          busy={update.isPending}
          onChange={(next) => update.mutate({ email_notifications: next })}
        />
      )}
      <p className="mt-4 flex items-center gap-1.5 text-xs text-text-secondary">
        <InformationCircleIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
        Account emails, such as password resets and invitations, are always sent.
      </p>
    </GradientCard>
  );
};

const NotificationsSettings: React.FC = () => (
  <section className="max-w-2xl space-y-6">
    <header>
      <h2 className="text-xl font-bold text-text-primary">Notifications</h2>
      <p className="mt-1 text-sm text-text-secondary">
        Choose which emails you get from this workspace. Marketing emails from Tereiko are under
        General.
      </p>
    </header>

    <EmailNotificationsCard />
  </section>
);

export default NotificationsSettings;
